import { useLayoutEffect, useRef } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';

import { FILTER_CHECKBOX_ITEM_HEIGHT_PX, FilterCheckboxItem } from './FilterCheckboxItem';

const OVERSCAN_ROWS = 10;

interface VirtualizedTagListProps {
  /** Already filtered and sorted. The caller owns search and empty states. */
  tags: string[];
  selectedTags: string[];
  /** Prefix for the checkbox `id` / label `htmlFor` pair: `account-tag` gives `account-tag-vip`. */
  idPrefix: string;
  onToggle: (tag: string, checked: boolean) => void;
  /** When this value changes the list scrolls back to the top. Pass the search text. */
  scrollResetKey: string;
}

export const VirtualizedTagList = ({
  tags,
  selectedTags,
  idPrefix,
  onToggle,
  scrollResetKey,
}: VirtualizedTagListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // The compiler skips this component; only plain values reach its children.
  // eslint-disable-next-line react-hooks/incompatible-library
  const rowVirtualizer = useVirtualizer({
    count: tags.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => FILTER_CHECKBOX_ITEM_HEIGHT_PX,
    overscan: OVERSCAN_ROWS,
    getItemKey: (index) => tags[index],
  });

  // Reset before paint when searching; preserve the position when toggling tags.
  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [scrollResetKey]);

  const virtualItems = rowVirtualizer.getVirtualItems();
  const firstRowOffset = virtualItems[0]?.start ?? 0;

  return (
    <div ref={scrollRef} className="max-h-80 overflow-y-auto">
      <div className="relative w-full" style={{ height: rowVirtualizer.getTotalSize() }}>
        <div
          className="absolute top-0 left-0 w-full"
          style={{ transform: `translateY(${firstRowOffset}px)` }}
        >
          {virtualItems.map((virtualRow) => {
            const tag = tags[virtualRow.index];

            return (
              <FilterCheckboxItem
                key={virtualRow.key}
                id={`${idPrefix}-${tag}`}
                checked={selectedTags.includes(tag)}
                onCheckedChange={(checked) => onToggle(tag, checked)}
              >
                {tag}
              </FilterCheckboxItem>
            );
          })}
        </div>
      </div>
    </div>
  );
};
